import {
  Box,
  Card,
  Button,
  Stack,
  Typography,
  Paper,
  IconButton,
  CardActionArea,
  Chip,
  Collapse,
  Divider,
  Avatar,
  Link,
  Tooltip,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ClipCard, { ClipCardProps, ConvertShareClipToClipCard } from "../../Components/Cards/ClipCard/clip-card";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import ReactPlayerWrapper from "../../Components/MediaPlayer/ReactPlayerWrapper/player-wrapper";
import { PlayingStatus } from "../../Components/MediaPlayer/ReactPlayerWrapper/playing-statuses";
import { useNavigate, useParams } from "react-router";
import { mediaService, shareService, thumbnailService } from "../../Core/Api/services";
import { DbShareMediaInfos } from "../../Core/Api/Types/db-share-media-infos";
import { PlayerTimeDuration } from "../../Utils/Timecode/ReactPlayer";
import { useAppDispatch, useAppSelector } from "../../Redux/redux-store";
import {
  ReactPlayerMarker,
  PlayPause,
  SetPlayerInput,
  SetPlayerAutoStart,
} from "../../Redux/Reducers/redux-slice-react-player";
import { routePaths } from "../../Router/router";
import { ThumbnailSize } from "../../Core/Api/Thumbnail/thumbnail-api-service";
import { Clip } from "../../Core/Api/Types/clip";
import { CircularProgress } from "@mui/material";
import { WatermarkTemplates } from "../../Core/Api/Types/watermark-templates";
import { ShareMediaType } from "../../Core/Api/Types/share-media-type";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { ObjectType } from "../../Core/Api/Types/object-type";
import PosterImage from "../../Components/Misc/PosterImage";
import Viewer from "react-viewer";
import { DocPreviewer } from "../admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview-docs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { PreviewContext } from "../admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { ExpandLessOutlined, ExpandMoreOutlined, NotificationsOutlined, TimerOutlined } from "@mui/icons-material";
import Panel from "../../Components/Panels/Panel/panel";
import UnauthDashboardLayout from "../../Layouts/UnauthDashboard/unauth-dashboard";
import {
  GetCreatingOrEditingTimelineEvent,
  GetTimelineClips,
  SetActiveTimeline,
  SetCreatingOrEditingTimelineEvent,
  SetFilteredTimelineClips,
  SetTimelineClips,
  TimelineType,
} from "../../Redux/Reducers/redux-slice-timeline";
import { PanelTimeline } from "../../Components/Panels/TimelinePanel/panel-timeline";
import { GetPlayerInfos, SetPlayerHeight, SetPlayerResponsive } from "../../Redux/Reducers/redux-slice-layout";
import { ClipContext } from "../../Core/Api/Types/clip-context";
import EmptyListSlot from "../../Components/Misc/EmptyListSlot";
import { UserHasAcl } from "../../Utils/helpers";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { useTranslation } from "react-i18next";
import useIsMobile from "../../Utils/Hooks/useIsMobile";

// #region type
export interface ClipDTO {
  id: number;
  userName: string;
  comment: string;
  date?: Date;
  timecodes: { in: ReactPlayerMarker; out: ReactPlayerMarker };
}

interface ShareDTO {
  views: number;
  viewsUntilExpire: number;
  clips: ClipDTO[];
  comment: string;
  name: string;
  isActive: boolean;
  key: string;
  mediaKey: string | null;
  watermarkText?: string;
  watermarkTpl?: WatermarkTemplates;
  type: ShareMediaType;
  hlsChunker: boolean;
  epNumber: number | undefined;
  objectType: ObjectType;
}

interface ShareMasterDTO {
  isMultiple: boolean;
  key: string;
  comment: string;
  name: string;
  endDate: Date;
  listShare: ShareDTO[];
  titleName: string;
  daysUntilExpire: number;
  createdByUserName: string;
  creationDate: Date;
}

export type ViewLinkBotPlayerProps = {
  children?: React.ReactNode;
  timeline?: React.ReactNode;
  share;
  selectedShare: ShareDTO;
  config;
  function?;
};
// #endregion type

// #region utils
export function shareClipToDTO(clips: Clip[]): ClipDTO[] {
  if (!clips) return [];

  return clips?.map<ClipDTO>((x) => {
    return {
      id: x.id,
      comment: x.comment!,
      userName: x.userName!,
      date: x.date || undefined,
      timecodes: {
        in: {
          markFrame: x.frameIn!,
          markTimecode: x.timecodeIn!,
          markTimecodeFile: x.timecodeInFile || undefined,
          isSet: true,
        },
        out: {
          markFrame: x.frameOut!,
          markTimecode: x.timecodeOut!,
          markTimecodeFile: x.timecodeOutFile || undefined,
          isSet: true,
        },
      },
    };
  });
}

function shareToDTO(share: DbShareMediaInfos, hlsChunker: boolean): ShareDTO {
  return {
    views: share.viewInfos?.length || 0,
    viewsUntilExpire: share.viewsUntilExpire || 0,
    key: share.key!,
    mediaKey: share.mediaKey,
    comment: share.comment || "",
    name: share.name!,
    isActive: share.isActive || false,
    clips: shareClipToDTO(share.clips || []),
    type: share.type ?? ShareMediaType.StreamingBasic,
    hlsChunker: hlsChunker,
    epNumber: share.episodeNb ?? undefined,
    objectType: share.objectType!,
  };
}
// #endregion utils

const ViewLink: React.FC<{}> = (props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [shareKey, SetPageShareKey] = React.useState<string>(useParams().key!);
  const [platformId, SetPlatformId] = React.useState<number>(0);
  const [selectedContent, setSelectedContent] = React.useState<ShareDTO>();
  // we do this to reload the object when selected content has changed
  const [currentContent, setCurrentContent] = React.useState<ShareDTO>();
  const share = React.useRef<ShareMasterDTO>(undefined);
  const config = {
    tabOpen: React.useState<boolean>(false), /// isMobile
    isCreating: React.useState<boolean>(false),
    isClipFocus: React.useState<boolean>(false),
    selectedTab: React.useState<string>("contents"),
    clipId: React.useState<number>(-1),
  };

  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (shareKey === "") {
      navigate(routePaths.viewLinkError.path + "/" + shareKey);
      return;
    }

    shareService
      .GetShareByKey(shareKey)
      .then((data) => {
        // Processing
        if (data.rescode === -3) {
          navigate(routePaths.viewLinkProcessing.path + "/" + shareKey, {
            state: {
              error: data.message,
            },
          });
          return;
        }

        // Not found
        if (data.rescode === -1) {
          navigate(routePaths.viewLinkError.path + "/" + shareKey, {
            state: {
              error: data.message,
            },
          });
          return;
        }

        // Manage expiration
        if (data.rescode !== 0) {
          navigate(routePaths.viewLinkExpired.path + "/" + shareKey, {
            state: {
              error: data.message,
            },
          });
          return;
        }

        if (!data.share?.isMultiple) data.share!.linkedShares = [data.share!];

        SetPlatformId(data.share?.platformId!);

        let shares: ShareDTO[] = data.share?.linkedShares?.map<ShareDTO>((share) =>
          shareToDTO(share, data.hlsChunker)
        ) || [shareToDTO(data.share!, data.hlsChunker)];

        let shareRef: ShareMasterDTO = {
          isMultiple: data.share?.isMultiple || false,
          key: data.share?.key!,
          comment: data.share?.comment || "",
          name: data.share?.name!,
          endDate: data.share?.endDate!,
          titleName: data.share?.titleName!,
          daysUntilExpire: data.share?.daysUntilExpire!,
          creationDate: data.share?.creationInfos?.date || new Date(),
          createdByUserName: data.share?.createdByUserName!,
          listShare: shares.sort((a, b) => (a.epNumber ?? -1) - (b.epNumber ?? -1)),
        };
        share.current = shareRef;

        let firstAvailable = share.current.listShare.find((x) => x.isActive);

        if (firstAvailable === undefined) {
          navigate(routePaths.viewLinkError.path + "/" + shareKey);
          return;
        }
        setSelectedContent(firstAvailable);
      })
      .catch(() => {
        navigate(routePaths.viewLinkError.path + "/" + shareKey);
      });
  }, [shareKey]);

  // Load selected content media & clips
  React.useEffect(() => {
    if (selectedContent === undefined) return;
    if (!selectedContent.isActive) navigate(routePaths.viewLinkExpired.path + "/" + shareKey);

    dispatch(SetPlayerAutoStart(config.selectedTab[0] !== "contents"));

    if (selectedContent?.mediaKey === null)
      shareService.GetShareByKey(selectedContent.key!).then((data) => {
        selectedContent.views = data.share?.viewInfos?.length || 0;
        selectedContent.viewsUntilExpire = data.share?.viewsUntilExpire || 0;
        selectedContent.mediaKey = data.share?.mediaKey || null;

        // autostart tweet to go to the timecode selecte.
        dispatch(SetPlayerInput({ shareMediaKey: selectedContent.mediaKey!, objectId: null, episode: null }));
      });
    else dispatch(SetPlayerInput({ shareMediaKey: selectedContent.mediaKey!, objectId: null, episode: null }));

    //updates clips for current selected content
    shareService.GetShareByKey(selectedContent.key).then((data) => {
      const clips: ClipCardProps[] = [];
      if (data.share && data.share.clips) {
        const clipsDTO = shareClipToDTO(data.share.clips);
        clipsDTO.map((clip) => {
          clips.push(ConvertShareClipToClipCard(clip));
        });

        dispatch(SetTimelineClips(clips));
        dispatch(SetFilteredTimelineClips(clips));
      }
    });

    // update selected clip object
    const contentIndex = share.current!.listShare.findIndex((content) => content.key === selectedContent.key);
    share.current!.listShare[contentIndex] = selectedContent;
    setCurrentContent(selectedContent);
  }, [selectedContent]);

  const assetPreview = React.useMemo(() => {
    switch (selectedContent?.objectType) {
      case ObjectType.Video:
      case ObjectType.Audio:
        return <ViewLinkPlayer selectedContent={selectedContent!} />;
      case ObjectType.Image:
        return <ImagePreview urlImage={mediaService.GetShareImagePreviewer(selectedContent.mediaKey!)} />;
      case ObjectType.Document:
        return (
          <DocPreviewer fileUrl={mediaService.DownloadShareMediaFile(selectedContent.mediaKey!)} fileExtension="" />
        );
      default:
        <Typography>{t("page:error.unsupportedObjectType")}</Typography>;
    }
  }, [share, selectedContent, config]);

  if (isMobile) {
    return (
      <Box sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            p: 0,
            mb: 0,
            height: "100%",
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {share.current !== undefined ? assetPreview : <MiddleSkeleton />}
          {share && share.current && currentContent && (
            <ViewLinkMobilePanel
              share={share.current!}
              selectedContent={currentContent}
              setSelectedContent={setSelectedContent}
            />
          )}
        </Box>
      </Box>
    );
  }
  return (
    <UnauthDashboardLayout
      transparentTopbar
      pageTitle={`Screener`}
      sxContent={{ display: "flex", flexDirection: "column", px: 0 }}
    >
      <Box sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            p: 0,
            mb: 0,
            height: "100%",
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {share.current !== undefined ? assetPreview : <MiddleSkeleton />}
          {share && share.current && currentContent && (
            <ViewLinkPanel
              share={share.current!}
              selectedContent={currentContent}
              setSelectedContent={setSelectedContent}
            />
          )}
        </Box>
      </Box>
    </UnauthDashboardLayout>
  );
};

const ViewLinkPanel: React.FC<{ share: ShareMasterDTO; selectedContent: ShareDTO; setSelectedContent: Function }> = ({
  share,
  selectedContent,
  setSelectedContent,
}) => {
  const { t } = useTranslation();
  const isCreatingTimelineEvent = useAppSelector(GetCreatingOrEditingTimelineEvent);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const notifyOwnerAboutClips = () => {
    shareService
      .NotifyClips({
        shareKey: share.key,
        userName: "Share screener App",
        clips: null,
      })
      .then((response) => {
        if (response.success) enqueueCustomSnackbar(t("alert:success.ownerHasBeenNotified"), "success");
        else enqueueCustomSnackbar(t("alert:warning.notifyingOwner"), "warning");
      });
  };

  return (
    <>
      {isCreatingTimelineEvent ? (
        <PanelTimeline parentId={-500} context={PreviewContext.Share} shareKey={selectedContent.key} />
      ) : (
        <Panel noWrapper open sx={{ height: "100%" }}>
          <Stack p={2} gap={2}>
            <Card variant="outlined" sx={{ display: "flex", alignItems: "center", p: 2 }}>
              <Box
                component="figure"
                width={54}
                height={72}
                borderRadius={0.25}
                mr={2}
                sx={(theme) => ({ img: theme.helpers(theme).fullImage })}
                overflow="hidden"
              >
                <img
                  src={thumbnailService.GetShareThumbRefCompressedByObjectId(ThumbnailSize.small, share.key)}
                  alt=""
                />
              </Box>
              <Stack mr="auto">
                <Typography variant="subtitle2" component="h2" fontWeight={600} mb={1}>
                  {share.titleName}
                </Typography>
                <Typography variant="body1" color="text.secondary" fontWeight={400} display="inherit">
                  <TimerOutlined fontSize="small" sx={{ mr: 0.5 }} />
                  {t("page:body.daysUntilExpiration", { count: share.daysUntilExpire })}
                </Typography>
              </Stack>
            </Card>
            <Stack
              direction="row"
              gap={1.5}
              alignItems="flex-start"
              px={2}
              py={2}
              sx={(theme) => ({ background: theme.palette.background.paper, borderRadius: 1 })}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: "backgroundAlpha.main",
                  color: "text.secondary",
                  fontSize: 12,
                }}
              >
                <Typography variant="body1" color="text.primary">
                  {share.createdByUserName.slice(0, 1)}
                </Typography>
              </Avatar>
              <Stack flexGrow={1}>
                <Typography component="span" fontWeight={500} color="text.primary">
                  {share.createdByUserName}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {share.comment !== "" ? share.comment : t("form:helper.noComment")}
                </Typography>
              </Stack>
              <Tooltip title={t("tooltip:title.notifyOwnerAboutClips")}>
                <IconButton color="ghost" size="small" onClick={(e) => notifyOwnerAboutClips()}>
                  <NotificationsOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
            <ContentsList share={share} selectedContent={selectedContent} setSelectedContent={setSelectedContent} />
          </Stack>
          <Divider />
          <ClipsList share={share} selectedContent={selectedContent} />
        </Panel>
      )}
    </>
  );
};
const ViewLinkMobilePanel: React.FC<{
  share: ShareMasterDTO;
  selectedContent: ShareDTO;
  setSelectedContent: Function;
}> = ({ share, selectedContent, setSelectedContent }) => {
  const { t } = useTranslation();

  return (
    <Stack p={2} gap={2}>
      <Card variant="outlined" sx={{ display: "flex", alignItems: "center", p: 2 }}>
        <Box
          component="figure"
          width={54}
          height={72}
          borderRadius={0.25}
          mr={2}
          sx={(theme) => ({ img: theme.helpers(theme).fullImage })}
          overflow="hidden"
        >
          <img src={thumbnailService.GetShareThumbRefCompressedByObjectId(ThumbnailSize.small, share.key)} alt="" />
        </Box>
        <Stack mr="auto">
          <Typography variant="subtitle2" component="h2" fontWeight={600} mb={1}>
            {share.titleName}
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight={400} display="inherit">
            <TimerOutlined fontSize="small" sx={{ mr: 0.5 }} />
            {t("page:body.daysUntilExpiration", { count: share.daysUntilExpire })}
          </Typography>
        </Stack>
      </Card>
      <Stack
        direction="row"
        gap={1.5}
        alignItems="flex-start"
        px={2}
        py={2}
        sx={(theme) => ({ background: theme.palette.background.paper, borderRadius: 1 })}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: "backgroundAlpha.main",
            color: "text.secondary",
            fontSize: 12,
          }}
        >
          <Typography variant="body1" color="text.primary">
            {share.createdByUserName.slice(0, 1)}
          </Typography>
        </Avatar>
        <Stack flexGrow={1}>
          <Typography component="span" fontWeight={500} color="text.primary">
            {share.createdByUserName}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {share.comment !== "" ? share.comment : "No comment"}
          </Typography>
        </Stack>
      </Stack>
      <ContentsList share={share} selectedContent={selectedContent} setSelectedContent={setSelectedContent} />
    </Stack>
  );
};

const ContentsList: React.FC<{ share: ShareMasterDTO; selectedContent: ShareDTO; setSelectedContent: Function }> = ({
  share,
  selectedContent,
  setSelectedContent,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [hover, setHover] = useState(false);
  const timelineClips = useAppSelector(GetTimelineClips);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hideList = () => {
    clearTimeout(timerRef.current!);
    timerRef.current = setTimeout(() => setHover(false), 500);
  };
  const showList = () => {
    clearTimeout(timerRef.current!);
    setHover(true);
  };

  return (
    <Stack onMouseEnter={(e) => showList()} onMouseLeave={(e) => hideList()} sx={{ position: "relative" }}>
      {share.listShare.length > 1 ? (
        <Collapse in={hover} collapsedSize={88} timeout={500}>
          <Stack gap={0.5} pb={4}>
            {share.listShare.map((content) => {
              return (
                <Card
                  variant="outlined"
                  key={content?.key!}
                  aria-selected={selectedContent.key === content.key}
                  sx={{ order: 1, ...(selectedContent.key === content.key && { order: 0, mb: 1.5 }) }}
                >
                  <CardActionArea
                    component="div"
                    onClick={() => {
                      setSelectedContent(content);
                    }}
                    sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}
                  >
                    <IconButton
                      sx={{ mr: 0.5 }}
                      onClick={(e) => {
                        if (selectedContent.key === share.key) {
                          dispatch(PlayPause());
                        }
                        setSelectedContent(content);
                      }}
                    >
                      {content.objectType === ObjectType.Video ||
                      content.objectType === ObjectType.ImageSequence ||
                      content.objectType === ObjectType.Audio ? (
                        <PlayingStatus isplaying={selectedContent.key === content.key} />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>

                    <Typography
                      mr={1}
                      sx={(t) => ({
                        textAlign: "left",
                        flexGrow: 1,
                        ...t.helpers(t).clampedText,
                      })}
                    >
                      {content.name}
                    </Typography>
                    {content.isActive ? (
                      <Typography variant="body2" color="text.tier" sx={{ flexShrink: 0, mr: 1 }}>
                        {t("modal:modalShareUpdate.body.viewLeft", { count: content?.viewsUntilExpire })}
                      </Typography>
                    ) : (
                      <Chip label={t("common:expired")} color="error" sx={{ mr: 1 }} />
                    )}
                    {(content.objectType === ObjectType.Video ||
                      content.objectType === ObjectType.ImageSequence ||
                      content.objectType === ObjectType.Audio) && (
                      <Stack spacing={0.5} alignItems="center" direction="row" color="primary.text" mr={1}>
                        <VideoLibraryOutlinedIcon fontSize="small" />
                        <Typography>
                          {selectedContent.key === content.key ? timelineClips.length : content.clips?.length || 0}
                        </Typography>
                      </Stack>
                    )}
                  </CardActionArea>
                </Card>
              );
            })}
            <Stack
              sx={(theme) => ({
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                width: "100%",
                alignItems: "center",
                background: theme.palette.background.default,
                zIndex: 2,
              })}
            >
              <Link href="#" underline="none" variant="body1" color="primary.text">
                +{share.listShare.length - 1} {t("common:lowerCaseOther")}
                {hover ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
              </Link>
            </Stack>
          </Stack>
        </Collapse>
      ) : (
        <Stack gap={1}>
          {share.listShare.map((content) => {
            return (
              <Card variant="outlined" key={content?.key!} aria-selected={selectedContent.key === content.key}>
                <CardActionArea
                  component="div"
                  onClick={() => {
                    setSelectedContent(content);
                  }}
                  sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}
                >
                  <IconButton
                    sx={{ mr: 0.5 }}
                    onClick={(e) => {
                      if (selectedContent.key === share.key) {
                        dispatch(PlayPause());
                      }
                      setSelectedContent(content);
                    }}
                  >
                    {content.objectType === ObjectType.Video ||
                    content.objectType === ObjectType.ImageSequence ||
                    content.objectType === ObjectType.Audio ? (
                      <PlayingStatus isplaying={selectedContent.key === content.key} />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>

                  <Typography
                    mr={1}
                    sx={(t) => ({
                      textAlign: "left",
                      flexGrow: 1,
                      ...t.helpers(t).clampedText,
                    })}
                  >
                    {content.name}
                  </Typography>
                  {content.isActive ? (
                    <Typography variant="body2" color="text.tier" sx={{ flexShrink: 0, mr: 1 }}>
                      {content?.viewsUntilExpire} {t("modal:modalShareUpdate.body.viewLeft")}
                    </Typography>
                  ) : (
                    <Chip label={t("common:expired")} color="error" sx={{ mr: 1 }} />
                  )}
                  {(content.objectType === ObjectType.Video ||
                    content.objectType === ObjectType.ImageSequence ||
                    content.objectType === ObjectType.Audio) && (
                    <Stack spacing={0.5} alignItems="center" direction="row" color="primary.text" mr={1}>
                      <VideoLibraryOutlinedIcon fontSize="small" />
                      <Typography>
                        {selectedContent.key === content.key ? timelineClips.length : content.clips?.length || 0}
                      </Typography>
                    </Stack>
                  )}
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

const ClipsList: React.FC<{ share: ShareMasterDTO; selectedContent: ShareDTO }> = ({ share, selectedContent }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const timelineClips = useAppSelector(GetTimelineClips);

  useEffect(() => {
    dispatch(SetFilteredTimelineClips(timelineClips));
  }, [timelineClips]);

  return (
    <Stack px={2} pt={2}>
      <Typography variant="h6" color="text.secondary" mb={2}>
        {t("common:clipsWithCount", { count: timelineClips.length })}
      </Typography>
      <Stack gap={1}>
        {timelineClips.length === 0 ? (
          <EmptyListSlot
            title={t("page:emptyListSlot.title.noClips")}
            icon={<VideoLibraryOutlinedIcon />}
            CTA={
              <Button
                size="small"
                onClick={(e) => {
                  // ajouté ?
                  dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }));
                }}
                disabled={!UserHasAcl(AclTypes.aclClipCreate)}
              >
                {t("common:createClip")}
              </Button>
            }
          />
        ) : (
          <>
            {timelineClips.map((clip, index) => (
              <ClipCard
                key={clip.id}
                selectable
                id={clip.id}
                context={ClipContext.Share}
                deleteCallback={() => {
                  shareService.DeleteClip(selectedContent.key, clip.id).then((response) => {
                    if (response.success) enqueueCustomSnackbar(t("alert:error.clipDeleted"), "success");
                    else enqueueCustomSnackbar(t("alert:error.deletingClip", { message: response.message }), "warning");
                  });
                  const clipsTemp = [...timelineClips];
                  clipsTemp.splice(timelineClips.indexOf(timelineClips[index]), 1);
                  dispatch(SetTimelineClips(clipsTemp));
                }}
                clipName={clip.clipDescription}
                createdBy={clip.createdBy}
                playable
                isPlaying={true}
                timecodes={clip.timecodes}
              />
            ))}
          </>
        )}
      </Stack>
    </Stack>
  );
};

const ViewLinkPlayer: React.FC<{ selectedContent: ShareDTO }> = ({ selectedContent }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const shareInfosContainer = React.useRef<HTMLDivElement>(null);
  const { containerHeight } = useAppSelector(GetPlayerInfos);
  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(SetActiveTimeline(TimelineType.Clips));
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      if (isMobile) {
        dispatch(SetPlayerResponsive(true));
        dispatch(SetPlayerHeight(null));
      } else {
        dispatch(SetPlayerResponsive(false));
        dispatch(SetPlayerHeight(containerHeight - event[0].borderBoxSize[0].blockSize));
      }
    });

    if (shareInfosContainer.current) resizeObserver.observe(shareInfosContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [shareInfosContainer.current, containerHeight]);

  return (
    <>
      <ReactPlayerWrapper
        assetType={selectedContent.objectType}
        context={PreviewContext.Share}
        shareKey={selectedContent.key}
      />
      <Stack
        ref={shareInfosContainer}
        direction="row"
        spacing={1}
        px={2}
        py={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack>
          <Typography variant="h5" component="h2" fontWeight={600} mb={0.25}>
            {selectedContent.name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5} mt={1}>
            <PlayerTimeDuration />
            <Stack direction="row" alignItems="center" spacing={0.5} color="text.tier">
              <VisibilityOutlinedIcon fontSize="small" />
              <Typography>{t("page:body.countViews", { count: selectedContent.views })}</Typography>
            </Stack>
            {selectedContent.objectType === ObjectType.Audio && (
              <Chip label={t("common:audioOnly")} color="info" size="small" />
            )}
          </Stack>
        </Stack>
        {!isMobile && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }))}
          >
            {t("button:createAClip")}
          </Button>
        )}
      </Stack>
    </>
  );
};

const MiddleSkeleton: React.FC = () => {
  return (
    <Box sx={{ height: "100vh" }}>
      <CircularProgress
        sx={{
          position: "absolute",
          top: "47%",
          bottom: "53%",
          left: "50%",
        }}
      />
    </Box>
  );
};

const ImagePreview = (props: { urlImage: string }) => {
  const { urlImage } = props;
  const [visible, setVisible] = React.useState(false);

  return (
    <>
      <Viewer
        visible={visible}
        noNavbar={true}
        zIndex={2001}
        onClose={() => {
          setVisible(false);
        }}
        images={[{ src: urlImage, alt: "Image viewer" }]}
      />
      <Box overflow="auto" height="100%" display="flex" flexDirection="column" width="100%">
        <Paper
          sx={{
            p: 1.5,
            borderRadius: 1,
            backgroundImage: "none",
          }}
        >
          <PosterImage
            img={{ src: urlImage, loading: "eager" }}
            onClick={() => {
              setVisible(true);
            }}
            sx={{ cursor: "pointer" }}
          />
        </Paper>
      </Box>
    </>
  );
};

export default ViewLink;
